/**
 * Section Property Calculations
 * 
 * Calculates elastic and plastic section moduli for different materials and shapes
 * NOTE: Backend duplicate exists in Tools/CrossSectionPropertyCalculator.cs - update both if formulas change
 */

import { CrossSection } from '../types'

/**
 * Calculate elastic section modulus W_el = I / y_max
 * Used for all materials: Steel, Concrete, Timber
 */
export function calculateElasticModulus(
  inertia: number,  // m⁴
  height: number    // mm
): number {
  const h = height / 1000  // Convert to m
  const y_max = h / 2
  return inertia / y_max  // m³
}

/**
 * Calculate plastic section modulus for rectangular section
 * W_pl = (b × h²) / 4
 * Used for: Steel (Class 1/2), NOT for concrete/timber
 */
export function calculatePlasticModulusRectangular(
  width: number,   // mm
  height: number   // mm
): number {
  const b = width / 1000   // Convert to m
  const h = height / 1000  // Convert to m
  return (b * h * h) / 4  // m³
}

/**
 * Calculate plastic section modulus for I-beam
 * W_pl = A_flange × (h - t_f) + (t_w × h_w²) / 4
 * Used for: Steel I/H sections
 */
export function calculatePlasticModulusIBeam(
  width: number,          // mm - flange width
  height: number,         // mm - total height
  webThickness: number,   // mm
  flangeThickness: number // mm
): number {
  const b = width / 1000
  const h = height / 1000
  const t_w = webThickness / 1000
  const t_f = flangeThickness / 1000
  
  // Web height between flanges
  const h_w = h - 2 * t_f
  
  // Flange area (one flange)
  const A_flange = b * t_f
  
  // Plastic modulus
  const W_pl = 2 * A_flange * (h / 2 - t_f / 2) + (t_w * h_w * h_w) / 4
  
  return W_pl  // m³
}

/**
 * Calculate all section properties for a cross-section based on material
 */
export function calculateSectionProperties(
  section: CrossSection,
  material: 'steel' | 'concrete' | 'timber'
): Partial<CrossSection> {
  const properties: Partial<CrossSection> = {}
  
  // Elastic modulus (all materials)
  properties.elasticModulusY = calculateElasticModulus(section.inertia, section.height)
  
  if (section.inertiaZ) {
    properties.elasticModulusZ = calculateElasticModulus(section.inertiaZ, section.width)
  }
  
  // Material-specific properties
  switch (material) {
    case 'steel':
      // Calculate plastic modulus for steel
      if (section.sectionType === 'rectangular') {
        properties.plasticModulusY = calculatePlasticModulusRectangular(section.width, section.height)
      } else if ((section.sectionType === 'I-beam' || section.sectionType === 'H-beam') &&
                 section.webThickness && section.flangeThickness) {
        properties.plasticModulusY = calculatePlasticModulusIBeam(
          section.width,
          section.height,
          section.webThickness,
          section.flangeThickness
        )
      }
      break
      
    case 'concrete': {
      // Calculate reinforcement properties for concrete
      const cover = section.effectiveCover || 40  // mm default
      const rho = section.reinforcementRatio || 0.015  // 1.5% default

      properties.reinforcementRatio = rho
      properties.effectiveCover = cover

      // Calculate reinforcement area and lever arm for moment capacity
      const effectiveDepth = section.height - cover  // d = h - cover (mm)
      const reinforcementAreaMm2 = rho * section.width * effectiveDepth  // As = ρ × b × d (mm²)
      const reinforcementAreaM2 = reinforcementAreaMm2 / 1e6  // Convert mm² to m²
      const leverArmMm = 0.9 * effectiveDepth  // z ≈ 0.9d (simplified, mm)
      const leverArmM = leverArmMm / 1000  // Convert mm to m

      properties.reinforcementAreaBottom = reinforcementAreaM2
      properties.reinforcementLeverArmBottom = leverArmM
      break
    }
      
    case 'timber':
      // Timber uses only elastic modulus (already calculated above)
      // No plastic modulus - semi-brittle material
      break
  }
  
  return properties
}

/**
 * Format section modulus for display (convert m³ to cm³ for readability)
 */
export function formatSectionModulus(modulusM3: number | undefined): string {
  if (modulusM3 === undefined) return '—'
  const modulusCm3 = modulusM3 * 1e6  // m³ to cm³
  return `${modulusCm3.toFixed(1)} cm³`
}

/**
 * Format inertia for display (convert m⁴ to cm⁴ for readability)
 */
export function formatInertia(inertiaM4: number | undefined): string {
  if (inertiaM4 === undefined) return '—'
  const inertiaCm4 = inertiaM4 * 1e8  // m⁴ to cm⁴
  return `${inertiaCm4.toFixed(1)} cm⁴`
}

